import { useState } from 'react';
import { Link } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { IDS } from '@/constants/testIds';
import { CheckCircle2 } from 'lucide-react';

const INITIAL = {
  business_name: '',
  contact_name: '',
  email: '',
  password: '',
  business_type: '',
  license_number: '',
  phone: '',
  shipping_address: '',
  monthly_volume: '',
};

export default function Apply() {
  const [form, setForm] = useState(INITIAL);
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(false);
  const [error, setError] = useState('');

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await api.post('/auth/apply', form);
      setOk(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  if (ok) {
    return (
      <div className="container-x py-24 max-w-2xl" data-testid={IDS.apply.successMessage}>
        <div className="text-center">
          <CheckCircle2 size={40} strokeWidth={1.2} style={{ color: 'var(--ije-pink)' }} className="mx-auto" />
          <h1 className="h-display text-3xl md:text-4xl mt-6">Application received.</h1>
          <p className="sans text-base text-neutral-500 mt-4 leading-relaxed">
            Thank you. We&rsquo;ll review your business details and be in touch within 1&ndash;2 business days.
            Once approved, you&rsquo;ll be able to sign in and see wholesale pricing.
          </p>
          <Link to="/" className="btn-outline mt-10 inline-flex">Return Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container-x py-16 md:py-24 max-w-3xl">
      <span className="eyebrow">New Account</span>
      <h1 className="h-display text-3xl md:text-5xl mt-4">Apply for a wholesale account.</h1>
      <p className="sans text-sm md:text-base text-neutral-500 mt-4 max-w-xl leading-relaxed">
        Applications are reviewed within 1–2 business days. Approved retailers receive a pricing tier
        and immediate access to the catalog.
      </p>

      <form onSubmit={submit} data-testid={IDS.apply.form} className="mt-14 grid gap-8 md:grid-cols-2">
        <div className="md:col-span-2">
          <label className="ije-label">Business Name *</label>
          <input required data-testid={IDS.apply.businessName} value={form.business_name} onChange={update('business_name')} className="ije-input" placeholder="Legal business name" />
        </div>
        <div>
          <label className="ije-label">Contact Name *</label>
          <input required data-testid={IDS.apply.contactName} value={form.contact_name} onChange={update('contact_name')} className="ije-input" placeholder="Primary buyer" />
        </div>
        <div>
          <label className="ije-label">Email *</label>
          <input required type="email" data-testid={IDS.apply.email} value={form.email} onChange={update('email')} className="ije-input" placeholder="you@shop.com" />
        </div>
        <div>
          <label className="ije-label">Password *</label>
          <input required type="password" minLength={6} data-testid={IDS.apply.password} value={form.password} onChange={update('password')} className="ije-input" placeholder="Min. 6 characters" />
        </div>
        <div>
          <label className="ije-label">Phone</label>
          <input data-testid={IDS.apply.phone} value={form.phone} onChange={update('phone')} className="ije-input" placeholder="(555) 555-5555" />
        </div>
        <div>
          <label className="ije-label">Business Type</label>
          <input data-testid={IDS.apply.businessType} value={form.business_type} onChange={update('business_type')} className="ije-input" placeholder="Retail store, e-commerce, distributor…" />
        </div>
        <div>
          <label className="ije-label">Resale / License Number</label>
          <input data-testid={IDS.apply.licenseNumber} value={form.license_number} onChange={update('license_number')} className="ije-input" placeholder="State resale cert. or business license #" />
        </div>
        <div className="md:col-span-2">
          <label className="ije-label">Shipping Address</label>
          <textarea rows={3} data-testid={IDS.apply.shippingAddress} value={form.shipping_address} onChange={update('shipping_address')} className="ije-input" placeholder="Street, city, state, zip" />
        </div>
        <div className="md:col-span-2">
          <label className="ije-label">Estimated Monthly Order Volume</label>
          <input data-testid={IDS.apply.monthlyVolume} value={form.monthly_volume} onChange={update('monthly_volume')} className="ije-input" placeholder="e.g. $2,500 / mo" />
        </div>

        {error && (
          <div className="md:col-span-2 sans text-sm" style={{ color: 'var(--ije-pink-dark)' }} data-testid="apply-error">
            {error}
          </div>
        )}

        <div className="md:col-span-2 flex items-center justify-between mt-4">
          <p className="sans text-xs text-neutral-500">
            Already have an account? <Link to="/login" className="link-pink">Sign in</Link>
          </p>
          <button type="submit" disabled={busy} className="btn-primary" data-testid={IDS.apply.submit}>
            {busy ? 'Submitting…' : 'Submit Application'}
          </button>
        </div>
      </form>
    </div>
  );
}
